import debounce from 'lodash.debounce';
import { useEffect, useRef, useState } from 'react';

const DEFAULT_DELAY = 300;
const INSTANT_EXECUTE_OFFSET = 3;
export function useDebouncedValue<T>(initialValue: T, delay = DEFAULT_DELAY) {
  const valueRef = useRef(initialValue);
  const [debouncedValue, setDebouncedValue] = useState(initialValue);
  const debounceValueRef = useRef(
    debounce((nextValue: T) => setDebouncedValue(nextValue), delay),
  );
  useEffect(() => {
    debounceValueRef.current = debounce(
      (nextValue: T) => setDebouncedValue(nextValue),
      delay,
    );
    return () => {
      debounceValueRef.current.cancel();
    };
  }, [delay]);
  const reset = () => {
    debounceValueRef.current.cancel();
    valueRef.current = initialValue;
    debounceValueRef.current(initialValue);
  };
  const setValue = (nextValue: T) => {
    valueRef.current = nextValue;
    debounceValueRef.current(nextValue);
  };
  const setImmediateValue = (nextValue: T) => {
    valueRef.current = nextValue;
  };
  return {
    valueRef,
    debouncedValue,
    setValue,
    setImmediateValue,
    reset,
  };
}
export default function useDebounce(minimumLength = 0) {
  const [value, setValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState(value);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const debounceValueRef = useRef(
    debounce((nextValue: string) => {
      setIsDebouncing(false);
      setDebouncedValue(nextValue);
    }, DEFAULT_DELAY),
  );
  const reset = () => {
    debounceValueRef.current.cancel();
    setValue('');
    setDebouncedValue('');
    setIsDebouncing(false);
  };
  useEffect(() => {
    if ((value?.length || 0) >= minimumLength) {
      debounceValueRef.current(value);
      if (value?.length === minimumLength + INSTANT_EXECUTE_OFFSET) {
        debounceValueRef.current.cancel();
        setIsDebouncing(false);
        setDebouncedValue(value);
      }
    } else {
      debounceValueRef.current.cancel();
      setDebouncedValue('');
      setIsDebouncing(false);
    }
  }, [minimumLength, value]);
  const onChangeText = (text: string) => {
    setIsDebouncing(true);
    setValue(text);
  };
  return {
    value,
    debouncedValue,
    reset,
    isDebouncing,
    onChangeText,
  };
}
